import { ApolloLink, Operation } from '@apollo/client';
import { FragmentDefinitionNode, SelectionNode } from 'graphql';
import { TransportTimeoutTier, transportTimeoutMs, withAbortTimeout } from './transport-timeout.helpers';

// Root fields bounded by a tier other than the default; see CLAUDE.md "Every transport is bounded".
const ROOT_FIELD_TIERS = new Map<string, TransportTimeoutTier>([
  ['recordUserSession', 'scoreSync'],
]);

// Root field names, looking through inline fragments and fragment spreads at the root.
const rootFieldNames = (
  selections: readonly SelectionNode[], fragments: Map<string, FragmentDefinitionNode>,
): string[] => selections.flatMap((selection) => {
  if (selection.kind === 'Field') return [selection.name.value];
  if (selection.kind === 'InlineFragment') return rootFieldNames(selection.selectionSet.selections, fragments);
  const fragment = fragments.get(selection.name.value);
  return fragment ? rootFieldNames(fragment.selectionSet.selections, fragments) : [];
});

// The one place a GraphQL operation's bound is chosen: the longest of its root fields' own tiers.
export const operationTimeoutMs = (operation: Operation): number => {
  const definitions = operation.query.definitions;
  const definition = definitions.find((candidate) => (
    candidate.kind === 'OperationDefinition'
      && (!operation.operationName || candidate.name?.value === operation.operationName)
  ));
  const fragments = new Map(definitions
    .filter((candidate): candidate is FragmentDefinitionNode => candidate.kind === 'FragmentDefinition')
    .map((fragment) => [fragment.name.value, fragment] as [string, FragmentDefinitionNode]));
  const selections = definition?.kind === 'OperationDefinition' ? definition.selectionSet.selections : [];
  const fieldTimeouts = rootFieldNames(selections, fragments)
    .map((name) => transportTimeoutMs(ROOT_FIELD_TIERS.get(name) ?? 'graphql'));
  return fieldTimeouts.length ? Math.max(...fieldTimeouts) : transportTimeoutMs('graphql');
};

type TimedRequestInit = RequestInit & { timeoutMs?: number; timeoutLabel?: string };

// Hands the chosen bound to fetchGraphQLWithTimeout; Apollo spreads fetchOptions into the fetch init.
export const createOperationTimeoutLink = () => new ApolloLink((operation, forward) => {
  const fetchOptions = operation.getContext().fetchOptions || {};
  operation.setContext({
    fetchOptions: { ...fetchOptions, timeoutMs: operationTimeoutMs(operation), timeoutLabel: operation.operationName },
  });
  return forward(operation);
});

export const fetchGraphQLWithTimeout: typeof fetch = (input, init) => {
  const { timeoutMs, timeoutLabel, ...fetchInit } = (init ?? {}) as TimedRequestInit;
  const label = `GraphQL ${timeoutLabel || 'request'}`;
  return withAbortTimeout(label, timeoutMs ?? transportTimeoutMs('graphql'), async (signal) => {
    const response = await fetch(input, { ...fetchInit, signal });
    // Read inside the bound: Apollo reads the body after fetch settles and drops an AbortError silently.
    const body = await response.text();
    return new Response(body, { status: response.status, statusText: response.statusText, headers: response.headers });
  }, fetchInit.signal);
};
